import { FilterPopover, type Align, type SortProps } from './FilterPopover';
import { DateInput } from './DateInput';
import { TimePicker } from './TimePicker';

/**
 * Inclusive range, either end optional: whole days (`YYYY-MM-DD`) or, with
 * "Include time", minutes (`YYYY-MM-DDTHH:mm`) — the To minute included.
 */
export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

interface Draft extends DateRangeValue {
  withTime: boolean;
}

const EMPTY: Draft = { from: null, to: null, withTime: false };

const hasTime = (value: string | null) => !!value?.includes('T');

/** Switches between days and minutes, keeping the span the range covers. */
function withTimeToggled(draft: Draft, withTime: boolean): Draft {
  if (withTime) {
    return {
      withTime,
      from: draft.from ? `${draft.from}T00:00` : null,
      to: draft.to ? `${draft.to}T23:59` : null,
    };
  }
  return { withTime, from: draft.from?.slice(0, 10) ?? null, to: draft.to?.slice(0, 10) ?? null };
}

/**
 * One end of the range: a yyyy-mm-dd day, plus a finite HH:mm picker when
 * time is on. The time stays disabled until a date is set, then starts
 * at `defaultTime`.
 */
function DateTimeField({
  label,
  value,
  withTime,
  defaultTime,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string | null;
  withTime: boolean;
  defaultTime: string;
  min?: string | null;
  max?: string | null;
  onChange: (next: string | null) => void;
}) {
  const date = value?.slice(0, 10) ?? '';
  const time = value && value.length > 10 ? value.slice(11, 16) : null;
  return (
    <div className="column-filter-field">
      <span className="column-filter-field-label">{label}</span>
      <div className="column-filter-datetime">
        <DateInput
          label={withTime ? `${label} date` : label}
          value={date || null}
          min={min?.slice(0, 10)}
          max={max?.slice(0, 10)}
          onChange={(nextDate) => onChange(!nextDate ? null : withTime ? `${nextDate}T${time ?? defaultTime}` : nextDate)}
        />
        {withTime && (
          <TimePicker
            label={`${label} time`}
            value={time}
            disabled={!date}
            onChange={(nextTime) => onChange(`${date}T${nextTime}`)}
          />
        )}
      </div>
    </div>
  );
}

interface Props extends SortProps {
  label: string;
  align?: Align;
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Offer the "Include time" switch (timestamp columns). */
  allowTime?: boolean;
  /** IANA zone the values are read in; shown next to the label and in the popover. */
  timeZone?: string;
}

/** Date or timestamp column: an inclusive from/to range of days, or of minutes. */
export function DateRangeFilterPopover({
  label,
  align,
  value,
  onChange,
  allowTime = false,
  timeZone,
  ...sortProps
}: Props) {
  return (
    <FilterPopover<Draft>
      label={label}
      hint={timeZone}
      align={align}
      {...sortProps}
      filterActive={!!value.from || !!value.to}
      value={{ ...value, withTime: hasTime(value.from) || hasTime(value.to) }}
      emptyValue={EMPTY}
      onChange={(v) => onChange({ from: v.from || null, to: v.to || null })}
      isEmpty={(d) => !d.from && !d.to}
      width="17rem"
      render={({ draft, setDraft }) => (
        <div className="column-filter-stack">
          <DateTimeField
            label="From"
            value={draft.from}
            withTime={draft.withTime}
            defaultTime="00:00"
            max={draft.to}
            onChange={(from) => setDraft({ ...draft, from })}
          />
          <DateTimeField
            label="To"
            value={draft.to}
            withTime={draft.withTime}
            defaultTime="23:59"
            min={draft.from}
            onChange={(to) => setDraft({ ...draft, to })}
          />
          {timeZone && (
            <p className="column-filter-note">
              {draft.withTime
                ? `Times are ${timeZone} time, to the minute. The To minute is included.`
                : `Days run from midnight to midnight, ${timeZone} time.`}
            </p>
          )}
          {allowTime && (
            <label className="column-filter-switch">
              <input
                type="checkbox"
                role="switch"
                checked={draft.withTime}
                onChange={(e) => setDraft(withTimeToggled(draft, e.target.checked))}
              />
              <span className="column-filter-switch-track" aria-hidden="true" />
              Include time
            </label>
          )}
        </div>
      )}
    />
  );
}
